import { useEffect, useRef, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { Info } from "@/components/ui/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/format";
import {
  getAccountObjectives,
  type AccountObjectives,
} from "@/lib/api/accounts";

/**
 * Trading Objectives panel — three progress-bar cards (Maximum loss
 * limit, Daily loss limit, Consistency score) plus a UTC-midnight
 * countdown to next daily reset. Mirrors the layout every funded prop
 * firm shows on its trader dashboard so a returning operator can
 * immediately read where the account sits against its rules.
 *
 * Data flow:
 *   • Mount + every 30 s → REST `/api/accounts/:id/objectives`
 *     (server-side derived from accounts + account_snapshots + trades).
 *   • `liveEquity` prop → triggers an extra refetch when the value
 *     changes by more than $5 since the last reading. Keeps the
 *     "Current result" / "Drawdown limit" rows reactive while a
 *     position is open without hammering the endpoint on every
 *     sub-second tick.
 *   • The countdown is purely client-side (setInterval 1 s) against
 *     `dailyResetAtMs`.
 *
 * The endpoint reuses the same starting-balance / DD-percent inputs
 * the chart consumes, so the bars and the chart's reference floors
 * always agree.
 */
export function TradingObjectives({
  accountId,
  liveEquity,
  className,
}: {
  accountId: string;
  liveEquity: number | null;
  className?: string;
}): ReactNode {
  const [data, setData] = useState<AccountObjectives | null>(null);
  const [loading, setLoading] = useState(true);
  const lastFetchedEquity = useRef<number | null>(null);

  // Initial fetch + 30s polling. Polling covers the closed-trade
  // consistency math which only changes on close, not on tick.
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const next = await getAccountObjectives(accountId);
        if (!cancelled) {
          setData(next);
          lastFetchedEquity.current = next.currentEquity;
        }
      } catch {
        // Endpoint returns null-safe values — failure is non-fatal.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchOnce();
    const iv = setInterval(fetchOnce, 30_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [accountId]);

  // Reactive refetch when live equity moves materially. Avoids
  // re-fetching on every micro-tick — the threshold matches the
  // resolution operators care about (whole-dollar DD usage).
  useEffect(() => {
    if (liveEquity == null) return;
    const last = lastFetchedEquity.current;
    if (last != null && Math.abs(liveEquity - last) < 5) return;
    lastFetchedEquity.current = liveEquity;
    let cancelled = false;
    void getAccountObjectives(accountId).then((next) => {
      if (!cancelled) setData(next);
    });
    return () => {
      cancelled = true;
    };
  }, [liveEquity, accountId]);

  // 1s countdown — purely cosmetic, doesn't trigger refetches.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  if (loading && !data) {
    return (
      <div
        className={cn(
          "min-w-0 overflow-hidden rounded-xl border border-border/40 bg-card/40 p-3 sm:p-4",
          className,
        )}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-sans text-sm font-medium text-foreground">
            Trading Objectives
          </h3>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-32 rounded-lg border border-border/30 bg-muted/20" />
          ))}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const server = data;
  const { consistency, dailyResetAtMs } = server;

  // Server math reads `account_snapshots` + `account_sleep_snapshots`
  // for the trough. When live equity has dipped BELOW the latest
  // persisted snapshot (sub-30s gap, or bot is mid-tick) the server's
  // trough is stale. Overlay liveEquity locally so the DD bars react
  // to the dip the same instant the operator sees it on the equity
  // curve. We only EXTEND usage — never shrink it — because once a
  // trough is hit, prop-firm DD never recovers.
  const liveDip =
    liveEquity != null && Number.isFinite(liveEquity)
      ? Math.max(0, server.startBalance - liveEquity)
      : 0;
  const todayDip =
    liveEquity != null && Number.isFinite(liveEquity)
      ? Math.max(0, server.dailyLoss.todayAnchor - liveEquity)
      : 0;

  const maxLoss = mergeBucket(server.maxLoss, liveDip);
  const dailyLoss = mergeBucket(server.dailyLoss, todayDip);
  const consistencyPctDisplay =
    consistency.totalProfits > 0
      ? Math.min(100, consistency.ratio * 100)
      : 0;

  return (
    <div
      className={cn(
        "min-w-0 overflow-hidden rounded-xl border border-border/40 bg-card/40 p-3 backdrop-blur-sm sm:p-4",
        className,
      )}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-sans text-sm font-medium text-foreground">
          Trading Objectives
        </h3>
        <ResetCountdown resetAtMs={dailyResetAtMs} now={now} />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <ObjectiveCard
          title="Maximum loss limit"
          tooltip={`Max drawdown across the account's lifetime. Breaching this floor kills the account. Floor: ${fmtMoney(server.maxLoss.floor)}.`}
          used={maxLoss.used}
          limit={maxLoss.limit}
          remaining={maxLoss.remaining}
          pct={maxLoss.pct}
          currentLabel="Current result"
          currentValue={maxLoss.used}
        />
        <ObjectiveCard
          title="Daily loss limit"
          tooltip={`Max drawdown allowed in today's UTC trading day. Resets at 00:00 UTC. Floor: ${fmtMoney(server.dailyLoss.floor)}.`}
          used={dailyLoss.used}
          limit={dailyLoss.limit}
          remaining={dailyLoss.remaining}
          pct={dailyLoss.pct}
          currentLabel="Current result"
          currentValue={dailyLoss.used}
        />
        <ConsistencyCard
          mostProfitableDay={consistency.mostProfitableDayPnl}
          totalProfits={consistency.totalProfits}
          thresholdPct={consistency.thresholdPct}
          displayPct={consistencyPctDisplay}
          passing={consistency.passing}
        />
      </div>
    </div>
  );
}

/**
 * Extend a server-supplied DD bucket with a fresh live dip.
 *
 * Prop-firm DD never recovers — once you hit a trough it counts
 * forever — so we only ever EXTEND `used`. Recomputes `remaining`
 * and `pct` against the new used value. When liveDip is 0 (no live
 * equity, or live equity is above the anchor), passes the server
 * bucket through unchanged.
 */
function mergeBucket(
  server: { used: number; limit: number; remaining: number; pct: number; floor: number },
  liveDip: number,
): { used: number; limit: number; remaining: number; pct: number } {
  const used = Math.max(server.used, liveDip);
  const remaining = Math.max(0, server.limit - used);
  const pct = server.limit > 0 ? Math.min(100, (used / server.limit) * 100) : 0;
  return { used, limit: server.limit, remaining, pct };
}

/* ── Building blocks ─────────────────────────────────────────── */

function ObjectiveCard({
  title,
  tooltip,
  used,
  limit,
  remaining,
  pct,
  currentLabel,
  currentValue,
}: {
  title: string;
  tooltip: string;
  used: number;
  limit: number;
  remaining: number;
  pct: number;
  currentLabel: string;
  currentValue: number;
}): ReactNode {
  return (
    <div className="rounded-lg border border-border/40 bg-muted/20 p-4">
      <div className="mb-3 flex items-center gap-1.5">
        <span className="font-sans text-sm font-medium text-foreground">{title}</span>
        <Tooltip>
          <TooltipTrigger
            type="button"
            aria-label={`About ${title}`}
            className="inline-flex cursor-help items-center text-muted-foreground/60 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:text-foreground"
          >
            <Info className="h-3 w-3" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-pretty leading-snug">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </div>
      <ProgressBar pct={pct} />
      <div className="mt-2 flex items-baseline justify-between text-[11px]">
        <span className="font-mono text-muted-foreground tabular-nums">
          {fmtMoney(used)} / {fmtMoney(limit)}
        </span>
        <span className="font-mono font-semibold tabular-nums text-foreground">
          {pct.toFixed(2)}%
        </span>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-1.5 text-xs">
        <Row label={currentLabel} value={fmtMoney(currentValue)} />
        <Row label="Drawdown limit" value={fmtMoney(limit)} />
        <Row label="Remaining" value={fmtMoney(remaining)} />
      </div>
    </div>
  );
}

function ConsistencyCard({
  mostProfitableDay,
  totalProfits,
  thresholdPct,
  displayPct,
  passing,
}: {
  mostProfitableDay: number;
  totalProfits: number;
  thresholdPct: number;
  displayPct: number;
  passing: boolean | null;
}): ReactNode {
  // Visual bar pegs at 100% to stay readable when the trader
  // briefly violates (e.g. after a single big day with little
  // other activity). The numeric label still shows the true
  // ratio so the operator can spot the magnitude.
  return (
    <div className="rounded-lg border border-border/40 bg-muted/20 p-4">
      <div className="mb-3 flex items-center gap-1.5">
        <span className="font-sans text-sm font-medium text-foreground">
          Consistency score
        </span>
        <Tooltip>
          <TooltipTrigger
            type="button"
            aria-label="About Consistency score"
            className="inline-flex cursor-help items-center text-muted-foreground/60 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:text-foreground"
          >
            <Info className="h-3 w-3" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-pretty leading-snug">
            {`No single trading day's profit may exceed ${thresholdPct.toFixed(0)}% of total closed profits, or the firm voids the payout.`}
          </TooltipContent>
        </Tooltip>
      </div>
      <ProgressBar pct={displayPct} />
      <div className="mt-2 flex items-baseline justify-between text-[11px]">
        <span className="font-mono text-muted-foreground tabular-nums">
          {(totalProfits > 0 ? displayPct : 0).toFixed(2)}% / {thresholdPct.toFixed(0)}%
        </span>
        <span className="font-mono font-semibold tabular-nums text-foreground">
          {totalProfits > 0 ? `${displayPct.toFixed(2)}%` : "—"}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-1.5 text-xs">
        <Row
          label="Most profitable day"
          value={fmtMoney(mostProfitableDay)}
        />
        <Row
          label="Total profits"
          value={fmtMoney(totalProfits)}
          color={
            totalProfits >= 0 ? "text-foreground" : "text-destructive"
          }
        />
        <Row
          label="Passing?"
          value={passing == null ? "—" : passing ? "Yes" : "No"}
          color={
            passing == null
              ? "text-muted-foreground"
              : passing
                ? "text-emerald-500"
                : "text-destructive"
          }
        />
      </div>
    </div>
  );
}

/** Orange→red gradient bar; matches the screenshot. */
function ProgressBar({ pct }: { pct: number }): ReactNode {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted/60">
      <motion.div
        className="h-full rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500"
        initial={false}
        animate={{ width: `${clamped}%` }}
        transition={{ type: "spring", stiffness: 80, damping: 18 }}
      />
    </div>
  );
}

function Row({
  label,
  value,
  color = "text-foreground",
}: {
  label: string;
  value: string;
  color?: string;
}): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("font-mono font-medium tabular-nums", color)}>
        {value}
      </span>
    </div>
  );
}

function ResetCountdown({
  resetAtMs,
  now,
}: {
  resetAtMs: number;
  now: number;
}): ReactNode {
  const remaining = Math.max(0, resetAtMs - now);
  const totalSeconds = Math.floor(remaining / 1000);
  const h = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
  const s = String(totalSeconds % 60).padStart(2, "0");
  return (
    <div className="flex items-center gap-2 rounded-md border border-border/40 bg-muted/30 px-2.5 py-1 text-[11px] text-muted-foreground">
      <span>Daily reset:</span>
      <span className="font-mono tabular-nums text-foreground">
        {h}:{m}:{s}
      </span>
    </div>
  );
}
